import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faInfoCircle, 
  faUpload, 
  faSpinner, 
  faTrash,
  faRedo,
  faExclamationTriangle,
  faEye,
  faImages,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';

// Import SampleCalculator instead of QuiltSetup
import SampleCalculator from '../components/SampleCalculator';

// Sample images 
import sample1 from '../Assets/sample1.jpg';
import sample2 from '../Assets/sample2.jpg';
import sample3 from '../Assets/sample3.jpg';
import sample4 from '../Assets/sample4.jpg';
import sample5 from '../Assets/sample5.jpg';
import sample6 from '../Assets/sample6.jpg';
import sample7 from '../Assets/sample7.jpg';
import sample8 from '../Assets/sample8.jpg';
import sample9 from '../Assets/sample9.jpg';
import sample10 from '../Assets/sample10.jpg';
import sample11 from '../Assets/sample11.jpg';
import sample12 from '../Assets/sample12.jpg';
import sample13 from '../Assets/sample13.jpg';
import sample14 from '../Assets/sample14.jpg';
import sample15 from '../Assets/sample15.jpg';
import sample16 from '../Assets/sample16.jpg';

import { API_ENDPOINTS } from '../config/apiConfig'; // Import API endpoints

const SamplesPage = () => {
  const navigate = useNavigate();
  const [activeLayout, setActiveLayout] = useState('4x4');
  const [sampleImages] = useState([
    sample1, sample2, sample3, sample4, sample5, 
    sample6, sample7, sample8, sample9, sample10,
    sample11, sample12, sample13, sample14, sample15, sample16
  ]);

  // Add state for user uploaded images
  const [uploadedImages, setUploadedImages] = useState([]);
  const [processingImages, setProcessingImages] = useState({});
  const [uploading, setUploading] = useState(false);
  const [useUploadedImages, setUseUploadedImages] = useState(false);
  const [showSamples, setShowSamples] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  
  // Add new states for calculator-based workflow
  const [calculatorComplete, setCalculatorComplete] = useState(false);
  const [quiltDimensions, setQuiltDimensions] = useState(null);
  const fileInputRef = useRef(null);

  // Use dynamic layout options that will be updated based on calculator results
  const [layoutOptions, setLayoutOptions] = useState({
    '4x4': { rows: 4, cols: 4 }
  });

  // Get current layout dimensions with safety check
  const currentLayout = layoutOptions[activeLayout] || Object.values(layoutOptions)[0];

  // Determine how many images to show based on current layout
  const requiredImages = currentLayout.rows * currentLayout.cols;

  // Function to determine appropriate grid size based on image count - updated to only use 2x2 and 3x3
  const determineGridSize = (imageCount) => {
    if (imageCount <= 4) return '4x4';
    return '4x4'; // Default to 3x3 for any number of images greater than 4
  };

  // Update active layout when uploaded images change
  useEffect(() => {
    if (uploadedImages.length > 0) {
      const suggestedLayout = determineGridSize(uploadedImages.length);
      // Ensure suggestedLayout exists in layoutOptions
      if (layoutOptions[suggestedLayout]) {
        setActiveLayout(suggestedLayout);
        setUseUploadedImages(true);
      }
    } else {
      setUseUploadedImages(false);
    }
  }, [uploadedImages]);

  // Handle file upload with backend processing
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Get the current required number of images
    const currentRequiredImages = currentLayout.rows * currentLayout.cols;
    
    // Get the number of slots left to fill
    const slotsLeft = currentRequiredImages - uploadedImages.length;
    
    // Only process as many files as we need to fill the grid
    const filesToProcess = files.slice(0, slotsLeft);
    
    if (filesToProcess.length === 0) {
      // No slots left to fill
      return;
    }
    
    setUploading(true);
    
    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      const id = Date.now() + i;
      
      // Add to processing state
      setProcessingImages(prev => ({
        ...prev,
        [id]: { file: file.name, progress: 0 }
      }));
      
      try {
        // Update processing progress
        setProcessingImages(prev => ({
          ...prev,
          [id]: { ...prev[id], progress: 20 }
        }));
        
        // Create form data for API request
        const formData = new FormData();
        formData.append('image', file);
        
        // Generate a unique session ID for this demo user
        const demoSessionId = `demo_${Date.now()}`;
        formData.append('session_id', demoSessionId);
        
        // Call the demo image processing API
        const response = await fetch(API_ENDPOINTS.PROCESS_DEMO_IMAGE, {
          method: 'POST',
          body: formData,
        });
        
        // Update progress
        setProcessingImages(prev => ({
          ...prev,
          [id]: { ...prev[id], progress: 60 }
        }));
        
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Update progress
        setProcessingImages(prev => ({
          ...prev,
          [id]: { ...prev[id], progress: 90 }
        }));
        
        if (!data.success) {
          throw new Error(data.message || 'Failed to process image');
        }
        
        // Add to uploaded images
        setUploadedImages(prev => [
          ...prev, 
          { 
            id, 
            originalName: file.name,
            url: data.url,
            processed: true,
            fallback: data.fallback || false
          }
        ]);
        
        // Final progress update
        setProcessingImages(prev => ({
          ...prev,
          [id]: { ...prev[id], progress: 100 }
        }));
        
        // Remove from processing after a delay
        setTimeout(() => {
          setProcessingImages(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
          });
        }, 1000);
        
      } catch (error) {
        console.error("Error processing image:", error);
        
        // Use client-side fallback processing
        setProcessingImages(prev => ({
          ...prev,
          [id]: { ...prev[id], progress: 70, error: true }
        }));
        
        // Process the image locally as fallback
        const processedUrl = await processClientSideImage(file);
        
        // Add the processed image to the list
        setUploadedImages(prev => [
          ...prev, 
          { 
            id, 
            originalName: file.name,
            url: processedUrl,
            processed: true,
            fallback: true
          }
        ]);
        
        // Remove from processing after a delay
        setTimeout(() => {
          setProcessingImages(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
          });
        }, 1000);
      }
    }
    
    setUploading(false);
    e.target.value = null; // Reset file input
  };

  // Client-side image processing as fallback
  const processClientSideImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create a canvas to crop the image (simulating t-shirt cropping)
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Make canvas square to simulate the T-shirt tile
          const size = Math.min(500, Math.max(img.width, img.height));
          canvas.width = size;
          canvas.height = size;
          
          // Fill with white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, size, size);
          
          // Calculate position to center the image
          let drawWidth = img.width;
          let drawHeight = img.height;
          let offsetX = 0;
          let offsetY = 0;
          
          // Maintain aspect ratio while fitting within the square
          if (img.width > img.height) {
            drawHeight = (img.height / img.width) * size;
            offsetY = (size - drawHeight) / 2;
          } else {
            drawWidth = (img.width / img.height) * size;
            offsetX = (size - drawWidth) / 2;
          }
          
          // Draw the image centered
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          
          // Return the processed image URL after a simulated delay
          setTimeout(() => {
            resolve(canvas.toDataURL('image/jpeg', 0.9));
          }, 800); // Simulate processing delay
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle image delete
  const handleDeleteImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  // Reset to sample images
  const resetToSamples = () => {
    setUploadedImages([]);
    setUseUploadedImages(false);
    setActiveLayout('3x3');
  };

  // Handle quilt dimensions change from SampleCalculator
  const handleDimensionsChange = (dimensions) => {
    console.log("Received dimensions from calculator:", dimensions);
    
    // Store the dimensions
    setQuiltDimensions(dimensions);
  };
  
  // Called when calculator "Calculate" button is clicked
  const handleCalculatorComplete = (isComplete, calculations) => {
    if (isComplete && calculations) {
      setCalculatorComplete(true);
      
      // Clear any existing uploaded images when dimensions change
      if (uploadedImages.length > 0) {
        setUploadedImages([]);
      }
      
      // Create layout key based on calculator results
      const layoutKey = `${calculations.columns}x${calculations.rows}`;
      
      // Update layout options with calculator-determined grid size
      const newLayoutOptions = {
        [layoutKey]: { rows: calculations.rows, cols: calculations.columns }
      };
      
      setLayoutOptions(newLayoutOptions);
      setActiveLayout(layoutKey);
      
      console.log(`Updated layout to ${layoutKey} based on calculator results`);
    }
  };

  // Calculate remaining slots for upload - with fixed calculation from current layout
  const getSlotsRemaining = () => {
    if (!calculatorComplete || !currentLayout) return 0;
    
    const currentRequiredImages = currentLayout.rows * currentLayout.cols;
    return Math.max(0, currentRequiredImages - uploadedImages.length);
  };
  
  const slotsRemaining = getSlotsRemaining();
  const gridFull = slotsRemaining === 0;

  // Get current images to display
  const getCurrentImages = () => {
    if (useUploadedImages) {
      return uploadedImages.slice(0, requiredImages);
    } else if (showSamples) { // Only return sample images if showSamples is true
      return sampleImages.slice(0, requiredImages);
    } else {
      return []; // Return empty array when neither uploaded nor samples should be shown
    }
  };

  // Get image display elements
  const getImageElements = () => {
    const images = getCurrentImages();
    const elements = [];
    
    for (let i = 0; i < requiredImages; i++) {
      const image = images[i];
      
      if (image) {
        elements.push(
          <div 
            key={i} 
            className="aspect-square overflow-hidden bg-white relative group"
          >
            <img
              src={useUploadedImages ? image.url : image}
              alt={`Tile ${i + 1}`}
              className="w-full h-full object-cover"
            />
            
            {useUploadedImages && (
              <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => handleDeleteImage(image.id)}
                  className="bg-red-500 text-white p-1 m-1 rounded-full"
                  title="Remove image"
                >
                  <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        );
      } else {
        // Empty placeholder
        elements.push(
          <div 
            key={i} 
            className="aspect-square bg-gray-100 flex items-center justify-center"
          >
            <span className="text-gray-400 text-xs">Empty</span>
          </div>
        );
      }
    }
    
    return elements;
  };

  // Create grid CSS styles based on current layout (with safety check)
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${currentLayout.cols}, 1fr)`,
    gridTemplateRows: `repeat(${currentLayout.rows}, 1fr)`,
    gap: '0', // Remove gap between tiles
    maxWidth: '600px', // Decreased from 900px to 600px
    margin: '0 auto'
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header section */}
      <div className="bg-zinc-300 px-6 py-4 flex items-center">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-black hover:text-gray-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          <span style={{ fontFamily: "Playfair Display" }}>Back to Home</span>
        </button>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 
          className="text-3xl font-bold mb-2 text-center"
          style={{ fontFamily: "Playfair Display" }}
        >
          Try Out Memory Quilt Demo
        </h1>
        
        <div className="flex items-center justify-center mb-4 gap-2 text-gray-600">
          <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500" />
          <p className="text-sm max-w-2xl text-center">
            First, use the calculator to determine your ideal quilt size. Then, upload exactly
            16 photos for your 4x4 layout.
          </p>
        </div>
        
        {/* Calculator Toggle Button - always visible */}
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setShowCalculator(!showCalculator)}
            className="px-4 py-2 rounded-md bg-[#F6E8E0] border border-black flex items-center justify-center font-medium"
            style={{ fontFamily: "Playfair Display" }}
          >
            <FontAwesomeIcon icon={faCalculator} className="mr-2" />
            {showCalculator ? "Hide Size Calculator" : "Show Size Calculator"}
          </button>
        </div>
        
        {/* Sample Calculator - replaces QuiltSetup */}
        {showCalculator && (
          <div className="mb-8">
            <SampleCalculator 
              onDimensionsChange={handleDimensionsChange}
              onComplete={handleCalculatorComplete}
            />
          </div>
        )}
        
        {/* Upload section - only shown after calculator is complete */}
        {calculatorComplete && (
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-6">
            <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "Playfair Display" }}>
              Upload Your Images
            </h2>
            
            <div className="mb-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*"
                multiple
                className="hidden"
                disabled={uploading || gridFull}
              />
              
              {!gridFull ? (
                <div>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className={`w-full sm:w-auto px-4 py-2 rounded-md ${
                      uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#9CAF88] hover:bg-[#8A9B79]'
                    } text-white flex items-center justify-center`}
                    disabled={uploading || gridFull}
                  >
                    {uploading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        Processing Images...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        Upload {slotsRemaining} {slotsRemaining === 1 ? 'Image' : 'Images'}
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-600 mt-1">
                    Your {activeLayout} grid requires exactly {currentLayout?.rows * currentLayout?.cols} images. 
                    {slotsRemaining > 0 ? ` Please upload ${slotsRemaining} more.` : ''}
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="font-medium">Grid Complete!</p>
                  </div>
                  <p className="text-sm mt-1">You've uploaded all {currentLayout?.rows * currentLayout?.cols} required images for your {activeLayout} grid.</p>
                </div>
              )}
            </div>
            
            {/* Show samples button - only show when no uploads yet */}
            {!useUploadedImages && uploadedImages.length === 0 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowSamples(true)}
                  className="px-4 py-2 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                >
                  <FontAwesomeIcon icon={faEye} className="mr-2" />
                  View Sample Layouts Instead
                </button>
              </div>
            )}
            
            {/* Processing status */}
            {Object.keys(processingImages).length > 0 && (
              <div className="bg-white p-3 rounded border border-gray-200 mb-3">
                <h3 className="text-sm font-medium mb-2">Processing Images:</h3>
                <div className="space-y-2 max-h-24 overflow-y-auto">
                  {Object.entries(processingImages).map(([id, info]) => (
                    <div key={id} className="flex items-center text-xs">
                      <div className="w-full max-w-xs flex-grow">
                        <div className="flex justify-between mb-1">
                          <span className="truncate">{info.file}</span>
                          <span>{info.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full ${info.error ? 'bg-red-500' : 'bg-blue-500'}`}
                            style={{ width: `${info.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Uploaded images stats */}
            {uploadedImages.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <div>
                  <span className="text-sm font-medium">
                    {uploadedImages.length} of {currentLayout.rows * currentLayout.cols} images uploaded
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {slotsRemaining > 0 ? `(${slotsRemaining} remaining)` : '(Grid complete!)'}
                  </span>
                </div>
                
                <button
                  onClick={resetToSamples}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <FontAwesomeIcon icon={faRedo} className="mr-1" />
                  Reset Images
                </button>
              </div>
            )}
          </div>
        )}

        {/* Show setup required message when calculator hasn't been used yet */}
        {!calculatorComplete && !showCalculator && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Please use the Size Calculator above to set up your quilt dimensions before uploading images.
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  <button 
                    onClick={() => setShowCalculator(true)} 
                    className="font-medium underline"
                  >
                    Show Calculator
                  </button>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Only show layout selection when samples are showing or user has uploaded images */}
        {(showSamples || useUploadedImages) && (
          <div className="flex justify-center gap-4 mb-8 flex-wrap">
            {Object.keys(layoutOptions).map((layout) => (
              <button
                key={layout}
                onClick={() => setActiveLayout(layout)}
                disabled={useUploadedImages && uploadedImages.length > 0}
                className={`px-4 py-2 rounded-md border ${
                  activeLayout === layout 
                    ? 'bg-[#F6E8E0] border-black' 
                    : 'bg-white border-gray-300 hover:bg-gray-50'
                } ${useUploadedImages ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{ fontFamily: "Playfair Display" }}
              >
                {layout} Grid
              </button>
            ))}
          </div>
        )}
        
        {/* Warning when not enough images */}
        {useUploadedImages && uploadedImages.length < requiredImages && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  You've uploaded {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''}, 
                  but a {activeLayout} grid requires {requiredImages} images. 
                  Empty placeholders will be shown.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quilt preview - Only show when there are images to display */}
        {(showSamples || uploadedImages.length > 0) && (
          <div className="bg-gray-100 p-6 rounded-lg mb-8">
            <div style={gridStyle}>
              {getImageElements()}
            </div>
          </div>
        )}

        {/* Empty state when no samples or uploads are shown */}
        {!showSamples && uploadedImages.length === 0 && (
          <div className="bg-gray-50 p-8 rounded-lg border border-gray-200 mb-8 text-center">
            <div className="text-gray-500 mb-4">
              <FontAwesomeIcon icon={faImages} className="text-5xl mb-4" />
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "Playfair Display" }}>
                No Images to Display
              </h3>
              <p className="text-gray-600 mb-4">
                Please upload your own photos or click "View Sample Layouts" to see how your memory quilt could look.
              </p>
            </div>
          </div>
        )}

        {/* Reset samples button */}
        {showSamples && !useUploadedImages && (
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setShowSamples(false)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <FontAwesomeIcon icon={faRedo} className="mr-1" />
              Hide Samples
            </button>
          </div>
        )}

        {/* Information section */}
        <div className="bg-[#F6E8E0] p-6 rounded-lg max-w-3xl mx-auto">
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ fontFamily: "Playfair Display" }}
          >
            How It Works
          </h2>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="font-bold">1.</div>
              <div>
                <span className="font-medium">Use the Calculator</span> - Determine the ideal grid size based on your quilt and tile dimensions
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="font-bold">2.</div>
              <div>
                <span className="font-medium">Upload Your Clothing</span> - Take photos of your t-shirts, jerseys, baby clothes, or other fabric items
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="font-bold">3.</div>
              <div>
                <span className="font-medium">AI Analyzes & Designs</span> - Our AI arranges your items into beautiful layouts considering colors and patterns
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="font-bold">4.</div>
              <div>
                <span className="font-medium">Customize Your Design</span> - Add borders, embellishments, and text to make it uniquely yours
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="font-bold">5.</div>
              <div>
                <span className="font-medium">Create Your Quilt</span> - Download your design and create it yourself or work with a professional quilter
              </div>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/sign-in')}
              className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              style={{ fontFamily: "Playfair Display" }}
            >
              Start Designing Your Quilt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplesPage;
